import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './style.css';
import { getAllProducts } from '../../../usecases/products-usecase';

/**
 * ProductSearchDropdown Component
 * 
 * @param {Object} props
 * @param {Object} props.data - Current XML line item data
 * @param {Function} props.onSelected - Callback when a product is selected (product, lineData)
 * @param {Function} [props.onChange] - Callback on input change
 * @param {Object} [props.style] - Custom styles for the input
 * @param {Object} props.profile - User profile (uid, cid)
 * @param {Array} props.products - Shared list of products from parent
 * @param {boolean} props.loading - Loading state for products
 * @param {Function} props.onCreate - Callback to trigger "Create New Product" flow
 */
const ProductSearchDropdown = ({
  data,
  onSelected,
  onChange = undefined,
  style = {},
  profile = {},
  products = [],
  loading = false,
  onCreate
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  // Fallback seguro en caso de que profile no tenga uid/cid directamente
  const uid = profile?.uid || localStorage.getItem('uid') || '';
  const cid = profile?.cid || localStorage.getItem('cid') || null;

  // Estados para scroll infinito y paginación
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const searchTimeout = useRef(null);

  // Carga inicial y reset al abrir el buscador
  const loadInitialProducts = async (searchQuery = '') => {
    setIsSearching(true);
    try {
      const res = await getAllProducts(uid, cid, searchQuery, 1, 10, true, true, false, null, null, null, true);
      const dataList = Array.isArray(res) ? res : (res.data || []);
      const enabledProducts = dataList.filter(p => p.enable !== false);
      setFilteredProducts(enabledProducts);
      setPage(1);
      setHasMore(dataList.length >= 10);
    } catch (e) {
      console.error("Error al cargar productos iniciales:", e);
    } finally {
      setIsSearching(false);
    }
  };

  // Carga más productos al hacer scroll
  const loadMoreProducts = async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    try {
      const res = await getAllProducts(uid, cid, query, nextPage, 10, true, true, false, null, null, null, true);
      const dataList = Array.isArray(res) ? res : (res.data || []);
      const enabledProducts = dataList.filter(p => p.enable !== false);
      
      if (enabledProducts.length > 0) {
        setFilteredProducts(prev => [...prev, ...enabledProducts]);
        setPage(nextPage);
        setHasMore(dataList.length >= 10);
      } else {
        setHasMore(false);
      }
    } catch (e) {
      console.error("Error al cargar más productos:", e);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Escuchar el scroll del portal de la lista
  const handleScroll = (e) => {
    const listEl = e.target;
    if (listEl.scrollHeight - listEl.scrollTop <= listEl.clientHeight + 15) {
      loadMoreProducts();
    }
  };

  // Sincronizar productos cuando la query cambie (con debounce)
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (open) {
      searchTimeout.current = setTimeout(() => {
        loadInitialProducts(query);
      }, 350);
    }
    return () => clearTimeout(searchTimeout.current);
  }, [query, open]);

  // ✅ Calcular posición de la lista justo debajo del input
  useEffect(() => {
    if (open && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, [open, query, filteredProducts]);

  // ✅ Cerrar al hacer click fuera
  useEffect(() => {
    const onDocClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        (!listRef.current || !listRef.current.contains(e.target))
      ) {
        setOpen(false);
        setIsFocused(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // ✅ Cerrar si se hace scroll fuera del dropdown
  useEffect(() => {
    const onScroll = (e) => {
      if (!open) return;
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
      setIsFocused(false);
      setQuery('');
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  function selectProduct(product) {
    if (!product) return;
    onSelected?.(product, data);
    setQuery('');
    setOpen(false);
    setIsFocused(false);
    dropdownRef.current?.querySelector('input')?.blur();
  }

  function handleChange(e) {
    const input = e.target.value;
    setQuery(input);
    setOpen(true);
    if (!input) {
      onSelected?.(null, data);
    }
    onChange?.(e);
  }

  const handleFocus = (e) => {
    setIsFocused(true);
    setOpen(true);
    const initialText = data?.relatedItem 
      ? (data.relatedItem.name || data.relatedItem.description || `${data.relatedItem.sku || ''} - ${data.relatedItem.name || ''}`)
      : '';
    setQuery(initialText);
    const inputEl = e.target;
    setTimeout(() => {
      inputEl?.select?.();
    }, 10);
  };

  const handleBlur = () => {
    // Si no está el menú abierto (ej. no se hizo clic en un ítem de la lista)
    setTimeout(() => {
      if (!open) {
        setIsFocused(false);
        setQuery('');
      }
    }, 150);
  };

  const displayValue = isFocused 
    ? query 
    : (data?.relatedItem ? `${data.relatedItem.sku ? data.relatedItem.sku + ' - ' : ''}${data.relatedItem.name || data.relatedItem.description || ''}` : query);

  return (
    <>
      <div ref={dropdownRef} className="product-search-dropdown-layout" onClick={(e) => e.stopPropagation()}>
        <input
          style={{ ...style, flex: 1 }}
          className={`product-search-input ${data?.matchStatus || ''}`}
          type="text"
          placeholder="Buscar producto..."
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {loading && (!data?.matchStatus || data.matchStatus === 'pending') && (
          <div className="match-loading-spinner" />
        )}
      </div>

      {open && createPortal(
        <ul
          ref={listRef}
          className="product-search-list"
          onScroll={handleScroll}
          style={{
            top: coords.top,
            left: coords.left,
            width: coords.width,
            zIndex: 10005
          }}
        >
          {isSearching && page === 1 ? (
            <li className="product-search-empty">Cargando productos...</li>
          ) : (
            <>
              {/* Opción de crear producto al principio */}
              {onCreate && (
                <li
                  className="product-search-item create-new"
                  title={`Crear "${query || data?.description || 'nuevo producto'}" como nuevo`}
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    onCreate?.(data);
                    setOpen(false);
                    setIsFocused(false);
                  }}
                  style={{
                    borderBottom: '1px solid var(--color-gray-20)',
                    color: 'var(--color-primary)',
                    fontWeight: 'bold'
                  }}
                >
                  ＋ Crear "{query || data?.description || 'nuevo producto'}" como nuevo
                </li>
              )}
              {filteredProducts.length === 0 ? (
                <li className="product-search-empty">No se encontraron productos</li>
              ) : (
                <>
                  {filteredProducts.map((p, i) => (
                    <li
                      key={p.id ?? `${i}-${p.sku}`}
                      className="product-search-item"
                      title={`${p.sku} - ${p.name}`}
                      onMouseDown={(ev) => {
                        ev.preventDefault();
                        selectProduct(p);
                      }}
                    >
                      <div className="item-label">
                        {p.sku} - {p.name}
                      </div>
                    </li>
                  ))}
                  {isLoadingMore && (
                    <li className="product-search-empty" style={{ padding: '8px 12px', fontSize: '0.8rem' }}>
                      Cargando más...
                    </li>
                  )}
                </>
              )}
            </>
          )}
        </ul>,
        document.body
      )}
    </>
  );
};

export default ProductSearchDropdown;
